import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DownloadIcon } from './icons'
import { currentOS, isBrowser, type OS } from '../lib/appEnv'
import { listDownloads, type AppDownload } from '../lib/downloads'

/** Ziyaretcinin OS'u -> indirilebilir platform (yoksa null: ios/linux). */
function osToPlatform(os: OS): AppDownload['platform'] | null {
  if (os === 'windows') return 'windows'
  if (os === 'macos') return 'macos'
  if (os === 'android') return 'android'
  return null
}

const PLATFORM_LABEL: Record<AppDownload['platform'], string> = {
  windows: 'settings.downloads.windows',
  macos: 'settings.downloads.macos',
  android: 'settings.downloads.android',
}

function megabytes(bytes: number, locale: string): string {
  const mb = bytes / (1024 * 1024)
  // Buyuk dosyada ondalik gurultu: "194 MB" yeterli; kucukte "3,0 MB".
  // FSI...PDI (U+2068/U+2069) yon yalitimi: Arapca (RTL) cumle icinde sayi
  // ile birim arasindaki bosluk ters yone cekilip "MB 194" gorunuyordu.
  const value = mb.toLocaleString(locale, { maximumFractionDigits: mb >= 10 ? 0 : 1 })
  return `⁨${value} MB⁩`
}

/**
 * Uygulama indirme cagrisi - ziyaretcinin isletim sistemine uygun olani
 * BUYUK ve ustte, digerlerini kucuk gosterir.
 *
 * YALNIZCA tarayicida gorunur (uygulamadan giren zaten kurmus - `isBrowser`).
 * `variant`:
 *  - `hero`: ana sayfa / belirgin blok (eyebrow + buyuk birincil + ikincil satir)
 *  - `inline`: ust bar icin tek, kompakt "bu sisteme indir" dugmesi
 *
 * Eslesen indirme yoksa (ios/linux veya o dosya henuz uretilmemis): `hero`
 * tum platformlari orta boy gosterir; `inline` hic gorunmez (ust bari
 * kalabaliklastirmamak icin).
 */
export function DownloadCta({
  variant = 'hero',
  showEyebrow = true,
}: {
  variant?: 'hero' | 'inline'
  showEyebrow?: boolean
}) {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<AppDownload[] | null>(null)

  useEffect(() => {
    if (!isBrowser()) return
    listDownloads()
      .then(setItems)
      .catch(() => setItems([]))
  }, [])

  const { primary, others } = useMemo(() => {
    const list = items ?? []
    const wanted = osToPlatform(currentOS())
    const primary = wanted ? (list.find((d) => d.platform === wanted) ?? null) : null
    const others = list.filter((d) => d !== primary)
    return { primary, others }
  }, [items])

  if (!isBrowser() || items === null || items.length === 0) return null

  const label = (d: AppDownload) => t(PLATFORM_LABEL[d.platform])

  if (variant === 'inline') {
    // Ust bar: yalnizca bu sisteme uygun tek dugme. Eslesme yoksa gizle.
    if (!primary) return null
    return (
      <a className="download-inline" href={primary.url} download>
        <DownloadIcon size={15} />
        <span>{t('settings.downloads.forOS', { os: label(primary) })}</span>
      </a>
    )
  }

  return (
    <div className="download-cta">
      {showEyebrow && (
        <span className="download-cta-eyebrow">{t('settings.downloads.heading')}</span>
      )}
      {primary ? (
        <>
          <a className="download-cta-primary" href={primary.url} download>
            <DownloadIcon size={20} />
            <span className="download-cta-primary-text">
              <strong>{t('settings.downloads.forOS', { os: label(primary) })}</strong>
              <small>
                {primary.version
                  ? t('settings.downloads.meta', {
                      version: primary.version,
                      size: megabytes(primary.size_bytes, i18n.language),
                    })
                  : megabytes(primary.size_bytes, i18n.language)}
              </small>
            </span>
          </a>
          {others.length > 0 && (
            <div className="download-cta-others">
              <span className="field-hint">{t('settings.downloads.other')}</span>
              {others.map((d) => (
                <a key={d.platform} className="download-cta-chip" href={d.url} download>
                  <DownloadIcon size={14} />
                  {label(d)}
                </a>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* iPhone/iPad icin henuz uygulama yok (TestFlight hazirlaniyor):
              bos bir "indir" yerine bugun ise yarayan yolu soyluyoruz - ana
              ekrana eklenen site uygulama gibi acilir (bkz. index.html PWA
              meta'lari). */}
          {currentOS() === 'ios' && (
            <p className="download-cta-ios-note">{t('settings.downloads.iosNote')}</p>
          )}
          {/* OS eslesmesi yok (ios/linux): hepsini orta boy goster. */}
          <div className="download-cta-grid">
            {items.map((d) => (
              <a key={d.platform} className="download-cta-tile" href={d.url} download>
                <DownloadIcon size={18} />
                <span>{label(d)}</span>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
